// src/Components/AdminBotsPage.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  TextField,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  RemoveCircleOutline as RemoveIcon,
  RocketLaunch as ProvisionIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiFetch from '../utils/api';

function AdminBotsPage() {
  const { user } = useAuth();

  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, bot: null });
  const [deleting, setDeleting] = useState(false);

  // Edit dialog state
  const [editDialog, setEditDialog] = useState({ open: false, bot: null });
  const [editForm, setEditForm] = useState({
    image: '',
    cpu_request: '',
    memory_request: '',
    cpu_limit: '',
    memory_limit: '',
    envRows: [], // [{ key, value }]
  });
  const [saving, setSaving] = useState(false);

  // Provision dialog state
  const [provisionDialog, setProvisionDialog] = useState(false);
  const [provisionForm, setProvisionForm] = useState({
    discordId: '',
    discordUsername: '',
    botName: '',
    discordToken: '',
    namespace: '',
    prefix: '!',
    blestaClientId: '',
    serviceId: '',
    botSlots: '1',
  });
  const [provisionLookup, setProvisionLookup] = useState(null); // { user, blestaLink }
  const [lookingUp, setLookingUp] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState(null);
  const [provisionSuccess, setProvisionSuccess] = useState(null);

  useEffect(() => {
    fetchBots();
  }, []);

  const fetchBots = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch('/api/admin/bots', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setBots(data);
      } else {
        throw new Error('Failed to fetch bots');
      }
    } catch (err) {
      console.error('Error fetching bots:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getBotId = (bot) => bot._id || `${bot.namespace}/${bot.name}`;
  const isGatewayBot = (bot) => bot.source === 'gateway';

  const handleBotAction = async (bot, action) => {
    try {
      let response;
      if (isGatewayBot(bot)) {
        response = await apiFetch(`/api/admin/gateway/namespaces/${bot.namespace}/bots/${bot.name}/${action}`, {
          method: 'POST',
          credentials: 'include',
        });
      } else {
        response = await apiFetch(`/api/admin/bots/${bot._id}/${action}`, {
          method: 'POST',
          credentials: 'include',
        });
      }

      if (response.ok) {
        fetchBots();
      } else {
        throw new Error(`Failed to ${action} bot`);
      }
    } catch (err) {
      console.error(`Error ${action}ing bot:`, err);
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    const bot = deleteDialog.bot;
    if (!bot) return;
    setDeleting(true);

    try {
      let response;
      if (isGatewayBot(bot)) {
        response = await apiFetch(`/api/bots/${bot.namespace}/${bot.name}`, {
          method: 'DELETE',
          credentials: 'include',
        });
      } else {
        response = await apiFetch(`/api/admin/bots/${bot._id}`, {
          method: 'DELETE',
          credentials: 'include',
        });
      }

      if (response.ok) {
        setBots(bots.filter(b => getBotId(b) !== getBotId(bot)));
        setDeleteDialog({ open: false, bot: null });
      } else {
        throw new Error('Failed to delete bot');
      }
    } catch (err) {
      console.error('Error deleting bot:', err);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // ─── Edit Dialog Logic ──────────────────────────────────────────────────────

  const openEditDialog = (bot) => {
    // Build env rows from bot.env_vars, excluding discord_token
    const envVars = bot.env_vars || {};
    const envRows = Object.entries(envVars)
      .filter(([k]) => k.toLowerCase() !== 'discord_token')
      .map(([key, value]) => ({ key, value: value || '' }));

    setEditForm({
      image: bot.image || '',
      cpu_request: bot.cpu_request || bot.resources?.requests?.cpu || '',
      memory_request: bot.memory_request || bot.resources?.requests?.memory || '',
      cpu_limit: bot.cpu_limit || bot.resources?.limits?.cpu || '',
      memory_limit: bot.memory_limit || bot.resources?.limits?.memory || '',
      envRows,
    });
    setEditDialog({ open: true, bot });
  };

  const handleEnvRowChange = (index, field, value) => {
    setEditForm(prev => {
      const rows = [...prev.envRows];
      rows[index] = { ...rows[index], [field]: value };
      return { ...prev, envRows: rows };
    });
  };

  const addEnvRow = () => {
    setEditForm(prev => ({
      ...prev,
      envRows: [...prev.envRows, { key: '', value: '' }],
    }));
  };

  const removeEnvRow = (index) => {
    setEditForm(prev => ({
      ...prev,
      envRows: prev.envRows.filter((_, i) => i !== index),
    }));
  };

  const handleSaveEdit = async () => {
    const bot = editDialog.bot;
    if (!bot) return;
    setSaving(true);

    try {
      // Build env_vars object from rows (skip empty keys)
      const env_vars = {};
      for (const row of editForm.envRows) {
        const key = row.key.trim();
        if (key && key.toLowerCase() !== 'discord_token') {
          env_vars[key] = row.value;
        }
      }

      const body = {
        env_vars,
        image: editForm.image,
        cpu_request: editForm.cpu_request,
        memory_request: editForm.memory_request,
        cpu_limit: editForm.cpu_limit,
        memory_limit: editForm.memory_limit,
      };

      const response = await apiFetch(
        `/api/admin/gateway/namespaces/${bot.namespace}/bots/${bot.name}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );

      if (response.ok) {
        setEditDialog({ open: false, bot: null });
        fetchBots();
      } else {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to update bot');
      }
    } catch (err) {
      console.error('Error saving bot:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ─── Provision Dialog Logic ─────────────────────────────────────────────────

  const openProvisionDialog = () => {
    setProvisionForm({
      discordId: '',
      discordUsername: '',
      botName: '',
      discordToken: '',
      namespace: '',
      prefix: '!',
      blestaClientId: '',
      serviceId: '',
      botSlots: '1',
    });
    setProvisionLookup(null);
    setProvisionError(null);
    setProvisionSuccess(null);
    setProvisionDialog(true);
  };

  const handleLookupUser = async () => {
    const discordId = provisionForm.discordId.trim();
    if (!discordId) return;
    setLookingUp(true);
    setProvisionError(null);
    setProvisionLookup(null);

    try {
      const res = await apiFetch(`/api/admin/users/lookup/${encodeURIComponent(discordId)}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to look up user');
      const data = await res.json();
      setProvisionLookup(data);

      // Auto-fill username if user exists
      if (data.user?.username) {
        setProvisionForm(prev => ({ ...prev, discordUsername: data.user.username }));
      }
      // Auto-fill blestaClientId if link exists
      if (data.blestaLink?.blestaClientId) {
        setProvisionForm(prev => ({ ...prev, blestaClientId: String(data.blestaLink.blestaClientId) }));
      }
      // Auto-select namespace if they have exactly one active service
      if (data.blestaLink?.services?.length > 0) {
        const activeServices = data.blestaLink.services.filter(s => s.status === 'active');
        if (activeServices.length === 1) {
          setProvisionForm(prev => ({ ...prev, namespace: activeServices[0].namespace }));
        }
      }
    } catch (err) {
      setProvisionError(err.message);
    } finally {
      setLookingUp(false);
    }
  };

  const handleProvision = async () => {
    setProvisioning(true);
    setProvisionError(null);
    setProvisionSuccess(null);

    try {
      const body = {
        discordId: provisionForm.discordId.trim(),
        discordUsername: provisionForm.discordUsername.trim(),
        botName: provisionForm.botName.trim(),
        discordToken: provisionForm.discordToken.trim(),
        namespace: provisionForm.namespace.trim(),
        prefix: provisionForm.prefix || '!',
      };

      // Include Blesta fields if no existing link
      if (!provisionLookup?.blestaLink) {
        body.blestaClientId = parseInt(provisionForm.blestaClientId, 10) || undefined;
        if (provisionForm.serviceId) body.serviceId = parseInt(provisionForm.serviceId, 10);
        if (provisionForm.botSlots) body.botSlots = parseInt(provisionForm.botSlots, 10);
      }

      const res = await apiFetch('/api/admin/provision', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to provision bot');
      }

      setProvisionSuccess('Bot provisioned successfully!');
      fetchBots();
      // Close after a short delay so user sees the success message
      setTimeout(() => {
        setProvisionDialog(false);
      }, 1500);
    } catch (err) {
      setProvisionError(err.message);
    } finally {
      setProvisioning(false);
    }
  };

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'success';
      case 'stopped': return 'default';
      case 'creating': return 'info';
      case 'pending': return 'info';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAvatarUrl = (owner) => {
    if (!owner?.avatar || !owner?.discordId) return null;
    return `https://cdn.discordapp.com/avatars/${owner.discordId}/${owner.avatar}.png?size=32`;
  };

  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
              All Bots
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Overview of all bot instances across all namespaces and services.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={openProvisionDialog} startIcon={<ProvisionIcon />}>
              Provision Bot
            </Button>
            <Button variant="outlined" onClick={fetchBots} startIcon={<RefreshIcon />}>
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {bots.length === 0 ? (
        <Paper sx={{ textAlign: 'center', py: 8, border: '1px dashed rgba(255,255,255,0.12)' }} variant="outlined">
          <Typography variant="h6" sx={{ mb: 1 }}>
            No bots found
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No bot instances are deployed across any namespace.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Namespace</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bots.map((bot) => (
                <TableRow key={getBotId(bot)} hover>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {bot.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {bot.owner?.username ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={getAvatarUrl(bot.owner)}
                          sx={{ width: 24, height: 24 }}
                        >
                          {bot.owner.username[0]}
                        </Avatar>
                        <Typography variant="body2">
                          {bot.owner.username}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Unknown
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {bot.namespace || bot.kubernetes?.namespace || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bot.status}
                      color={getStatusColor(bot.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(bot.created_at || bot.createdAt)}</TableCell>
                  <TableCell align="right">
                    {isGatewayBot(bot) && (
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(bot)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {bot.status === 'stopped' || bot.status === 'creating' || bot.status === 'pending' ? (
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleBotAction(bot, 'start')}
                        title="Start"
                      >
                        <StartIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleBotAction(bot, 'stop')}
                        title="Stop"
                      >
                        <StopIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleBotAction(bot, 'restart')}
                      title="Restart"
                    >
                      <RefreshIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteDialog({ open: true, bot })}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => !deleting && setDeleteDialog({ open: false, bot: null })}>
        <DialogTitle>Delete Bot</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteDialog.bot?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, bot: null })} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Provision Bot Dialog */}
      <Dialog
        open={provisionDialog}
        onClose={() => !provisioning && setProvisionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Provision Bot for User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {provisionError && (
              <Alert severity="error" onClose={() => setProvisionError(null)}>
                {provisionError}
              </Alert>
            )}
            {provisionSuccess && (
              <Alert severity="success">{provisionSuccess}</Alert>
            )}

            {/* Step 1: Look up Discord user */}
            <Typography variant="subtitle2">1. Discord User</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Discord User ID"
                size="small"
                fullWidth
                value={provisionForm.discordId}
                onChange={(e) => setProvisionForm(prev => ({ ...prev, discordId: e.target.value }))}
                placeholder="e.g. 123456789012345678"
              />
              <Button
                variant="outlined"
                onClick={handleLookupUser}
                disabled={!provisionForm.discordId.trim() || lookingUp}
                startIcon={lookingUp ? <CircularProgress size={16} /> : <SearchIcon />}
                sx={{ minWidth: 100 }}
              >
                Lookup
              </Button>
            </Box>

            {provisionLookup && (
              <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid', borderColor: 'divider' }}>
                {provisionLookup.user ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Avatar
                      src={provisionLookup.user.avatar && provisionLookup.user.discordId
                        ? `https://cdn.discordapp.com/avatars/${provisionLookup.user.discordId}/${provisionLookup.user.avatar}.png?size=32`
                        : undefined}
                      sx={{ width: 24, height: 24 }}
                    >
                      {provisionLookup.user.username?.[0] || '?'}
                    </Avatar>
                    <Typography variant="body2">{provisionLookup.user.username}</Typography>
                    <Chip label="Existing user" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                  </Box>
                ) : (
                  <Box sx={{ mb: 1 }}>
                    <Chip label="New user — will be created" size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
                  </Box>
                )}
                {provisionLookup.blestaLink ? (
                  <Box>
                    <Chip label={`Blesta Client #${provisionLookup.blestaLink.blestaClientId}`} size="small" sx={{ height: 20, fontSize: '0.7rem', mr: 1 }} />
                    {provisionLookup.blestaLink.services.map((s, i) => (
                      <Chip
                        key={i}
                        label={`${s.namespace} (${s.status})`}
                        size="small"
                        color={s.status === 'active' ? 'success' : 'default'}
                        sx={{ height: 20, fontSize: '0.7rem', mr: 0.5 }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Chip label="No Blesta link — will create one" size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
              </Box>
            )}

            {/* Username if new user */}
            {provisionLookup && !provisionLookup.user && (
              <TextField
                label="Discord Username"
                size="small"
                fullWidth
                value={provisionForm.discordUsername}
                onChange={(e) => setProvisionForm(prev => ({ ...prev, discordUsername: e.target.value }))}
                helperText="Required for new users"
              />
            )}

            {/* Step 2: Blesta link (if none exists) */}
            {provisionLookup && !provisionLookup.blestaLink && (
              <>
                <Typography variant="subtitle2">2. Blesta Account</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField
                    label="Blesta Client ID"
                    size="small"
                    value={provisionForm.blestaClientId}
                    onChange={(e) => setProvisionForm(prev => ({ ...prev, blestaClientId: e.target.value }))}
                    type="number"
                  />
                  <TextField
                    label="Service ID (optional)"
                    size="small"
                    value={provisionForm.serviceId}
                    onChange={(e) => setProvisionForm(prev => ({ ...prev, serviceId: e.target.value }))}
                    type="number"
                  />
                  <TextField
                    label="Bot Slots"
                    size="small"
                    value={provisionForm.botSlots}
                    onChange={(e) => setProvisionForm(prev => ({ ...prev, botSlots: e.target.value }))}
                    type="number"
                  />
                </Box>
              </>
            )}

            {/* Step 3: Bot details */}
            <Typography variant="subtitle2">
              {provisionLookup && !provisionLookup.blestaLink ? '3.' : '2.'} Bot Configuration
            </Typography>
            <TextField
              label="Bot Name"
              size="small"
              fullWidth
              value={provisionForm.botName}
              onChange={(e) => setProvisionForm(prev => ({ ...prev, botName: e.target.value }))}
              helperText="Lowercase, alphanumeric and hyphens only"
            />
            <TextField
              label="Discord Bot Token"
              size="small"
              fullWidth
              type="password"
              value={provisionForm.discordToken}
              onChange={(e) => setProvisionForm(prev => ({ ...prev, discordToken: e.target.value }))}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {/* Namespace: dropdown if existing services, text field otherwise */}
              {provisionLookup?.blestaLink?.services?.length > 0 ? (
                <TextField
                  label="Namespace"
                  size="small"
                  select
                  value={provisionForm.namespace}
                  onChange={(e) => setProvisionForm(prev => ({ ...prev, namespace: e.target.value }))}
                  SelectProps={{ native: true }}
                >
                  <option value="">Select namespace...</option>
                  {provisionLookup.blestaLink.services
                    .filter(s => s.status === 'active')
                    .map((s, i) => (
                      <option key={i} value={s.namespace}>{s.namespace}</option>
                    ))}
                </TextField>
              ) : (
                <TextField
                  label="Namespace"
                  size="small"
                  value={provisionForm.namespace}
                  onChange={(e) => setProvisionForm(prev => ({ ...prev, namespace: e.target.value }))}
                  placeholder="e.g. discord-1-12345"
                />
              )}
              <TextField
                label="Command Prefix"
                size="small"
                value={provisionForm.prefix}
                onChange={(e) => setProvisionForm(prev => ({ ...prev, prefix: e.target.value }))}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProvisionDialog(false)} disabled={provisioning}>
            Cancel
          </Button>
          <Button
            onClick={handleProvision}
            variant="contained"
            disabled={
              provisioning ||
              !provisionForm.discordId.trim() ||
              !provisionForm.botName.trim() ||
              !provisionForm.discordToken.trim() ||
              !provisionForm.namespace.trim()
            }
            startIcon={provisioning ? <CircularProgress size={16} /> : <ProvisionIcon />}
          >
            {provisioning ? 'Provisioning...' : 'Provision'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Bot Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => !saving && setEditDialog({ open: false, bot: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Bot: {editDialog.bot?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Image"
              fullWidth
              size="small"
              value={editForm.image}
              onChange={(e) => setEditForm(prev => ({ ...prev, image: e.target.value }))}
            />

            <Typography variant="subtitle2" sx={{ mt: 1 }}>Resources</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="CPU Request"
                size="small"
                placeholder="e.g. 100m"
                value={editForm.cpu_request}
                onChange={(e) => setEditForm(prev => ({ ...prev, cpu_request: e.target.value }))}
              />
              <TextField
                label="CPU Limit"
                size="small"
                placeholder="e.g. 500m"
                value={editForm.cpu_limit}
                onChange={(e) => setEditForm(prev => ({ ...prev, cpu_limit: e.target.value }))}
              />
              <TextField
                label="Memory Request"
                size="small"
                placeholder="e.g. 128Mi"
                value={editForm.memory_request}
                onChange={(e) => setEditForm(prev => ({ ...prev, memory_request: e.target.value }))}
              />
              <TextField
                label="Memory Limit"
                size="small"
                placeholder="e.g. 512Mi"
                value={editForm.memory_limit}
                onChange={(e) => setEditForm(prev => ({ ...prev, memory_limit: e.target.value }))}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="subtitle2">Environment Variables</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addEnvRow}>
                Add
              </Button>
            </Box>
            {editForm.envRows.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No environment variables (discord_token is hidden)
              </Typography>
            )}
            {editForm.envRows.map((row, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="KEY"
                  value={row.key}
                  onChange={(e) => handleEnvRowChange(idx, 'key', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  placeholder="value"
                  value={row.value}
                  onChange={(e) => handleEnvRowChange(idx, 'value', e.target.value)}
                  sx={{ flex: 2 }}
                />
                <IconButton size="small" onClick={() => removeEnvRow(idx)} color="error">
                  <RemoveIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, bot: null })} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminBotsPage;
